<script setup lang="ts">
import { computed, ref } from 'vue';
import { useMutation, useQuery } from '@tanstack/vue-query';
import { erpIntegrationApi, type SalesOrderRow } from '@/api/erp-integration';
import { apiErrorCode } from '@/utils/api-errors';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import { careerLabel } from '@/utils/career-metadata';
import ErpIncorporationDialog from './erp-incorporation-dialog.vue';
import { actionError } from './erp-messages';
import { formatRange } from './erp-format';

// Every Sales order waiting for a coordinator: a patient to bring in (or to
// tell apart), or a patient's Requirements to confirm.

const emit = defineEmits<{ changed: [message?: string] }>();

const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 25 });

const { data, isPending } = useQuery({
  queryKey: ['erpIntegration', 'salesOrders'],
  queryFn: erpIntegrationApi.salesOrders,
});
const rows = computed(() => data.value ?? []);

const headers = [
  { title: 'Presupuesto', key: 'reference' },
  { title: 'Paciente', key: 'patientLabel' },
  // Wide enough for both buttons on one line.
  { title: 'Acciones', key: 'actions', sortable: false, align: 'end' as const, width: 112 },
];

// Sales orders of one patient under the same name, incorporated together.
const groupSizes = computed(() => rows.value.reduce<Record<string, number>>((sizes, row) => {
  if (row.group) sizes[row.group] = (sizes[row.group] ?? 0) + 1;
  return sizes;
}, {}));

// Why a patient's Sales order is back for a coordinator: lines Odoo added,
// or a quantity or notes Odoo changed.
function comebackText(row: SalesOrderRow): string {
  const added = [...new Set(row.addedLines.map((line) => (
    line.career ? careerLabel(line.career) : line.productName ?? 'producto sin nombre'
  )))];
  const parts = added.length ? [`Línea nueva en Odoo: ${added.join(', ')}`] : [];
  if (row.changes.includes('quantity')) parts.push('Cambió la cantidad vendida');
  if (row.changes.includes('notes')) parts.push('Cambiaron las notas');

  return parts.join(' · ');
}

const incorporating = ref<SalesOrderRow | null>(null);
const dismissing = ref<SalesOrderRow | null>(null);
const dismissError = ref('');

const { mutate: dismiss, isPending: isDismissing } = useMutation({
  mutationFn: (row: SalesOrderRow) => erpIntegrationApi.dismiss(row.id),
  onSuccess: () => {
    dismissing.value = null;
    emit('changed', '¡Presupuesto descartado!');
  },
  onError: (error) => {
    dismissError.value = actionError(apiErrorCode(error),
      'Ocurrió un error al descartar. Intenta nuevamente.');
  },
});

function openDismiss(row: SalesOrderRow) {
  dismissError.value = '';
  dismissing.value = row;
}

function onIncorporated(message: string) {
  incorporating.value = null;
  emit('changed', message);
}
</script>
<template>
  <div class="pa-4">
    <h2 class="text-h6 mb-1">
      Presupuestos por incorporar
    </h2>
    <p class="mb-4 text-body-2 text-medium-emphasis">
      Presupuestos vigentes que esperan a un coordinador. Al incorporar uno, Kuido crea o vincula
      al paciente con sus requerimientos y desde esa fecha envía sus sesiones a Odoo; los de un
      mismo nombre se incorporan juntos. Los de pacientes ya incorporados se confirman.
    </p>
    <v-data-table
      v-bind="tableProps"
      :header-props="{ class: 'font-weight-bold' }"
      :headers="headers"
      :items="rows"
      :loading="isPending"
      loading-text="Cargando los presupuestos..."
      no-data-text="No hay presupuestos en esta lista"
      density="compact"
    >
      <template #[`item.reference`]="{ item }">
        <div class="font-weight-medium">
          {{ item.reference ?? '—' }}
        </div>
        <div class="text-caption text-medium-emphasis">
          {{ formatRange(item.startsOn, item.endsOn) }}
        </div>
      </template>
      <template #[`item.patientLabel`]="{ item }">
        <div>{{ item.patient?.name ?? item.patientLabel ?? 'Sin nombre' }}</div>
        <div
          v-if="item.suggestedPatient"
          class="text-caption text-medium-emphasis"
        >
          Podría ser {{ item.suggestedPatient.name }}, ya en Kuido
        </div>
        <div
          v-else-if="item.reason === 'ambiguous_patient'"
          class="text-caption text-medium-emphasis"
        >
          Más de un paciente de Kuido tiene este nombre
        </div>
        <div
          v-else-if="item.patient && comebackText(item)"
          class="text-caption text-medium-emphasis"
        >
          {{ comebackText(item) }}
        </div>
        <v-chip
          v-if="item.group && (groupSizes[item.group] ?? 0) > 1"
          size="x-small"
          label
          class="mt-1"
        >
          {{ groupSizes[item.group] }} presupuestos con este nombre
        </v-chip>
      </template>
      <template #[`item.actions`]="{ item }">
        <div class="d-flex flex-nowrap justify-end ga-2 py-1">
          <v-tooltip
            location="top"
            :text="item.patient ? 'Confirmar' : 'Incorporar'"
          >
            <template #activator="{ props: tooltipProps }">
              <v-btn
                v-bind="tooltipProps"
                :icon="item.patient ? 'mdi-check' : 'mdi-plus'"
                size="small"
                variant="tonal"
                color="primary"
                :aria-label="`${item.patient ? 'Confirmar' : 'Incorporar'} el presupuesto ` +
                  `${item.reference ?? ''}`"
                @click="incorporating = item"
              />
            </template>
          </v-tooltip>
          <v-tooltip
            v-if="!item.patient"
            location="top"
            text="Descartar"
          >
            <template #activator="{ props: tooltipProps }">
              <v-btn
                v-bind="tooltipProps"
                icon="mdi-delete-outline"
                size="small"
                variant="text"
                color="error"
                :aria-label="`Descartar el presupuesto ${item.reference ?? ''}`"
                @click="openDismiss(item)"
              />
            </template>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>
    <erp-incorporation-dialog
      v-if="incorporating"
      :order-id="incorporating.id"
      @close="incorporating = null"
      @done="onIncorporated"
    />
    <responsive-dialog
      :model-value="!!dismissing"
      title="Descartar presupuesto"
      :mobile-fullscreen="false"
      max-width="560"
      @update:model-value="dismissing = null"
    >
      <p class="text-body-1">
        El presupuesto {{ dismissing?.reference }} no se incorporará a Kuido y no volverá a
        aparecer. Úsalo para presupuestos que no son de atención domiciliaria, pruebas o
        duplicados.
      </p>
      <v-alert
        v-if="dismissError"
        type="error"
        variant="tonal"
        density="compact"
        class="mt-4"
      >
        {{ dismissError }}
      </v-alert>
      <template #actions>
        <v-spacer />
        <v-btn
          variant="text"
          @click="dismissing = null"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="error"
          :loading="isDismissing"
          @click="dismissing && dismiss(dismissing)"
        >
          Descartar
        </v-btn>
      </template>
    </responsive-dialog>
  </div>
</template>
